import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

// The Schedule page's Resources pop-up (Phelipe 2026-09-22): the list of
// crews, trades, equipment and people a task can be assigned to. The
// Resources column on the Gantt could always pick from this list — there
// was just no way to put anything on it. Kept apart from gantt_controller
// so it can be tested without Bryntum: it only talks to the Gantt through
// two events, `schedule-resources:request` (give me the current list) and
// `schedule-resources:apply` (here is the new one); the Gantt does the
// saving, with the schedule's own sync.
const MAX_NAME = 50

export default class extends Controller {
  static targets = ["modal", "list", "input", "teamButton", "empty", "saveButton"]
  static values = { team: Array, text: Object }

  connect() {
    this.rows = []
    this._onProvide = (event) => this._show(event.detail?.resources || [])
    window.addEventListener("schedule-resources:provide", this._onProvide)
  }

  disconnect() {
    window.removeEventListener("schedule-resources:provide", this._onProvide)
  }

  // ── Open / close ────────────────────────────────────────────────────

  // The toolbar button lives inside the Gantt's element, so it opens this
  // through gantt#openResources, which answers with `provide`. This action
  // is for anything placed inside this element.
  open() {
    window.dispatchEvent(new CustomEvent("schedule-resources:request"))
  }

  _show(resources) {
    this.rows = resources.map((r) => ({ id: r.id, name: r.name, original: r.name, assigned: r.assigned || 0 }))
    this.removed = []
    this.modalTarget.classList.remove("hidden")
    this._render()
    this.inputTarget.value = ""
    this.inputTarget.focus()
  }

  hideModal() {
    this.modalTarget.classList.add("hidden")
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  escape(event) {
    if (event.key === "Escape" && !this.modalTarget.classList.contains("hidden")) this.hideModal()
  }

  // ── Editing the list ────────────────────────────────────────────────

  add(event) {
    event?.preventDefault()
    this._addName(this.inputTarget.value)
    this.inputTarget.value = ""
    this.inputTarget.focus()
  }

  addOnEnter(event) {
    if (event.key === "Enter") this.add(event)
  }

  addTeam() {
    let added = 0
    this.teamValue.forEach((name) => { if (this._addName(name, { silent: true })) added += 1 })
    this._render()
    if (added === 0) this._flash(this.textValue.team_all_there)
  }

  rename(event) {
    const row = this.rows[Number(event.target.dataset.index)]
    if (row) row.name = event.target.value
  }

  remove(event) {
    const index = Number(event.currentTarget.dataset.index)
    const row = this.rows[index]
    if (!row) return
    if (row.id != null) this.removed.push(row.id)
    this.rows.splice(index, 1)
    this._render()
  }

  _addName(raw, { silent = false } = {}) {
    const name = raw.trim().replace(/\s+/g, " ").slice(0, MAX_NAME)
    if (!name) return false
    if (this.rows.some((r) => r.name.trim().toLowerCase() === name.toLowerCase())) {
      if (!silent) this._flash(this.textValue.already_there.replace("%{name}", name))
      return false
    }
    this.rows.push({ id: null, name, original: null, assigned: 0 })
    if (!silent) this._render()
    return true
  }

  // ── Save ────────────────────────────────────────────────────────────

  save() {
    const cleaned = this.rows
      .map((r) => ({ ...r, name: r.name.trim().replace(/\s+/g, " ").slice(0, MAX_NAME) }))
      .filter((r) => r.name)
    const seen = new Set()
    const resources = cleaned.filter((r) => {
      const key = r.name.toLowerCase()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    const detail = {
      added: resources.filter((r) => r.id == null).map((r) => r.name),
      renamed: resources.filter((r) => r.id != null && r.name !== r.original).map((r) => ({ id: r.id, name: r.name })),
      removed: this.removed
    }
    window.dispatchEvent(new CustomEvent("schedule-resources:apply", { detail }))
    track("Schedule Resources Updated", { added: detail.added.length, renamed: detail.renamed.length, removed: detail.removed.length })
    this.hideModal()
  }

  // ── Drawing ─────────────────────────────────────────────────────────

  _render() {
    const list = this.listTarget
    list.replaceChildren()
    this.emptyTarget.classList.toggle("hidden", this.rows.length > 0)
    this.rows.forEach((row, index) => {
      const line = document.createElement("div")
      line.className = "flex items-center gap-2"
      line.dataset.resourceRow = ""

      const input = document.createElement("input")
      input.type = "text"
      input.value = row.name
      input.maxLength = MAX_NAME
      input.dataset.index = index
      input.setAttribute("data-action", "input->schedule-resources#rename")
      input.className = "flex-1 min-w-0 rounded-lg border border-gray-300 bg-white text-sm px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand"
      line.append(input)

      if (row.assigned > 0) {
        const count = document.createElement("span")
        count.className = "shrink-0 text-[13px] text-gray-500 whitespace-nowrap"
        count.textContent = this.textValue.assigned.replace("%{count}", row.assigned)
        line.append(count)
      }

      const remove = document.createElement("button")
      remove.type = "button"
      remove.dataset.index = index
      remove.setAttribute("data-action", "click->schedule-resources#remove")
      remove.setAttribute("aria-label", this.textValue.remove)
      remove.title = row.assigned > 0 ? this.textValue.remove_assigned.replace("%{count}", row.assigned) : this.textValue.remove
      remove.className = "shrink-0 w-7 h-7 inline-flex items-center justify-center rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 cursor-pointer transition-colors"
      remove.innerHTML = '<svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/></svg>'
      line.append(remove)
      list.append(line)
    })
    this.teamButtonTarget.classList.toggle("hidden", this.teamValue.length === 0)
  }

  _flash(message) {
    const note = document.createElement("div")
    note.className = "text-[13px] text-amber-700 mt-1"
    note.textContent = message
    this.inputTarget.parentElement.append(note)
    setTimeout(() => note.remove(), 2500)
  }
}
